import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch, adminCompanyPath } from '../../lib/api-client';
import { useCompany } from '../../contexts/CompanyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

interface SellerInfo {
  id: string;
  uuid: string;
  code: string;
  name: string;
  bankCode: string;
  accountNo: string;
  accountName: string;
  status: string;
  payinFeePercent: number;
  payoutFeePercent: number;
  syncedAt: string | null;
  lastStatSyncAt: string | null;
  hasAccount: boolean;
  username: string | null;
}

interface StatItem {
  id: string;
  date: string;
  deposit: number;
  depositCount: number;
  topup: number;
  topupCount: number;
  withdraw: number;
  withdrawCount: number;
  settle: number;
  settleCount: number;
  hold: number;
  feeSeller: number;
  feePlatform: number;
  fee: number;
}

interface SubSellerItem {
  id: string;
  name: string;
  payinSharePercent: number;
  payoutSharePercent: number;
  level: number;
  status: string;
  username: string | null;
}

interface CreateSubSellerResult {
  username: string;
  tempPassword: string;
  name: string;
}

function CreateSubSellerDialog({
  sellerId,
  companyId,
  onClose,
  onCreated,
}: {
  sellerId: string;
  companyId: string;
  onClose: () => void;
  onCreated: (result: CreateSubSellerResult) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    username: '',
    payinSharePercent: '',
    payoutSharePercent: '',
    parentId: sellerId,
  });
  const [serverError, setServerError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      apiPost<CreateSubSellerResult>(
        adminCompanyPath(`/sellers/${sellerId}/sub-sellers`, companyId),
        {
          name: form.name,
          username: form.username,
          payinSharePercent: parseFloat(form.payinSharePercent) || 0,
          payoutSharePercent: parseFloat(form.payoutSharePercent) || 0,
          parentId: form.parentId,
          password: '',
        },
      ),
    onSuccess: (res) => {
      if (res.ok) {
        onCreated(res.data);
        onClose();
      } else {
        setServerError(res.error.message);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    createMutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('seller.createSubSeller')}
        </h3>
        {serverError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
            {serverError}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('sellers.name')}</label>
            <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('auth.username')}</label>
            <Input value={form.username} onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))} required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('sellers.payinFee')} %</label>
            <Input type="number" step="0.01" min="0" max="100" value={form.payinSharePercent} onChange={(e) => setForm((p) => ({ ...p, payinSharePercent: e.target.value }))} required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('sellers.payoutFee')} %</label>
            <Input type="number" step="0.01" min="0" max="100" value={form.payoutSharePercent} onChange={(e) => setForm((p) => ({ ...p, payoutSharePercent: e.target.value }))} required />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={createMutation.isPending} className="flex-1">
              {createMutation.isPending ? t('common.loading') : t('seller.createSubSeller')}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CredentialResultDialog({
  result,
  onClose,
}: {
  result: CreateSubSellerResult;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    const text = t('sellers.credentialCopyFormat', {
      name: result.name,
      username: result.username,
      password: result.tempPassword,
    });
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('sellers.credentialTitle')}
        </h3>
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          {t('sellers.credentialWarning')}
        </div>
        <div className="mb-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('sellers.name')}</label>
            <div className="font-medium text-gray-900 dark:text-gray-100">{result.name}</div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('auth.username')}</label>
            <div className="rounded bg-gray-100 px-3 py-2 font-mono text-sm dark:bg-gray-700 dark:text-gray-100">{result.username}</div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('sellers.tempPassword')}</label>
            <div className="rounded bg-gray-100 px-3 py-2 font-mono text-sm font-bold dark:bg-gray-700 dark:text-gray-100">{result.tempPassword}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={copyToClipboard} className="flex-1">
            {copied ? t('sellers.copied') : t('sellers.copyCredentials')}
          </Button>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
        </div>
      </div>
    </div>
  );
}

function FeeEditor({
  seller,
  companyId,
  onUpdated,
}: {
  seller: SellerInfo;
  companyId: string;
  onUpdated: () => void;
}) {
  const { t } = useTranslation();
  const [payinFee, setPayinFee] = useState(String(seller.payinFeePercent));
  const [payoutFee, setPayoutFee] = useState(String(seller.payoutFeePercent));
  const [message, setMessage] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: (data: { payinFeePercent?: number; payoutFeePercent?: number }) =>
      apiPatch(adminCompanyPath(`/sellers/${seller.id}`, companyId), data),
    onSuccess: (res) => {
      if (res.ok) {
        setMessage(t('common.saved'));
        onUpdated();
        setTimeout(() => setMessage(null), 2000);
      } else {
        setMessage(res.error.message);
      }
    },
  });

  const handleSave = () => {
    const payinVal = parseFloat(payinFee) || 0;
    const payoutVal = parseFloat(payoutFee) || 0;
    setMessage(null);
    saveMutation.mutate({ payinFeePercent: payinVal, payoutFeePercent: payoutVal });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('sellers.feeSettings')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('sellers.payinFee')} (%)
            </label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={payinFee}
              onChange={(e) => setPayinFee(e.target.value)}
              className="w-28"
            />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              {t('sellers.payinFeeDesc')}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('sellers.payoutFee')} (%)
            </label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={payoutFee}
              onChange={(e) => setPayoutFee(e.target.value)}
              className="w-28"
            />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              {t('sellers.payoutFeeDesc')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? t('common.loading') : t('common.save')}
            </Button>
            {message && (
              <span className={`text-xs ${message === t('common.saved') ? 'text-green-600' : 'text-red-600'}`}>
                {message}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatNumber(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatStatDate(isoDate: string): string {
  return new Date(isoDate).toISOString().split('T')[0];
}

function getMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  // Last 12 months
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    options.push({ value, label });
  }
  return options;
}

function padDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDateRangeForMonth(yearMonth: string): { dateFrom: string; dateTo: string; syncDate: string } {
  const [year, month] = yearMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0); // last day of month
  const now = new Date();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const endDate = isCurrentMonth ? now : lastDay;
  return {
    dateFrom: `${yearMonth}-01`,
    dateTo: padDate(endDate),
    syncDate: new Date(year, month - 1, 1).toISOString(),
  };
}

export function SellerDetail() {
  const { t } = useTranslation();
  const { sellerId } = useParams<{ sellerId: string }>();
  const navigate = useNavigate();
  const { activeCompanyId } = useCompany();

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const monthOptions = getMonthOptions();
  const { dateFrom, dateTo, syncDate } = getDateRangeForMonth(selectedMonth);
  const granularity = 'daily';

  const { data: sellerData } = useQuery({
    queryKey: ['admin', 'seller', sellerId],
    queryFn: () => apiGet<SellerInfo>(adminCompanyPath(`/sellers/${sellerId}`, activeCompanyId!)),
    enabled: !!activeCompanyId && !!sellerId,
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['admin', 'seller-stats', sellerId, dateFrom, dateTo, granularity],
    queryFn: () =>
      apiGet<{ items: StatItem[]; total: number }>(
        adminCompanyPath(`/sellers/${sellerId}/stats`, activeCompanyId!),
        { dateFrom, dateTo, granularity, pageSize: '100' },
      ),
    enabled: !!activeCompanyId && !!sellerId,
  });

  const { data: subSellersData, isLoading: subSellersLoading } = useQuery({
    queryKey: ['admin', 'seller-sub-sellers', sellerId],
    queryFn: () =>
      apiGet<{ items: SubSellerItem[] }>(
        adminCompanyPath(`/sellers/${sellerId}/sub-sellers`, activeCompanyId!),
      ),
    enabled: !!activeCompanyId && !!sellerId,
  });

  const queryClient = useQueryClient();
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [showCreateSubSeller, setShowCreateSubSeller] = useState(false);
  const [credentialResult, setCredentialResult] = useState<CreateSubSellerResult | null>(null);
  const [subSellerCredential, setSubSellerCredential] = useState<{ username: string; tempPassword: string; subSellerName: string } | null>(null);

  const resetSubSellerPwMutation = useMutation({
    mutationFn: (subSellerId: string) =>
      apiPost<{ username: string; tempPassword: string; subSellerName: string }>(
        adminCompanyPath(`/sellers/${sellerId}/sub-sellers/${subSellerId}/reset-password`, activeCompanyId!),
      ),
    onSuccess: (res) => {
      if (res.ok) {
        setSubSellerCredential(res.data);
      }
    },
  });

  const syncStatsMutation = useMutation({
    mutationFn: () =>
      apiPost<{ synced: number; errors: string[] }>(
        adminCompanyPath(`/sync/stats/${sellerId}`, activeCompanyId!),
        { queryParams: { date: syncDate } },
      ),
    onSuccess: (res) => {
      if (res.ok) {
        setSyncResult(JSON.stringify(res.data, null, 2));
        queryClient.invalidateQueries({ queryKey: ['admin', 'seller-stats', sellerId] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'seller', sellerId] });
      } else {
        setSyncResult(res.error.message);
      }
    },
    onError: (err) => {
      setSyncResult((err as Error).message);
    },
  });

  const seller = sellerData?.ok ? sellerData.data : null;
  const stats = statsData?.ok ? statsData.data.items : [];
  const subSellers = subSellersData?.ok ? subSellersData.data.items : [];

  if (!activeCompanyId) {
    return (
      <div className="p-6 text-amber-800 dark:text-amber-200">{t('company.noCompanySelected')}</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/sellers')}>
            &larr; {t('sellers.title')}
          </Button>
          {seller && (
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{seller.name}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <span className="font-mono">{seller.code}</span>
                {' '}&middot;{' '}
                <Badge variant={seller.status === 'active' ? 'success' : 'danger'}>
                  {t(`common.${seller.status}`)}
                </Badge>
                {' '}&middot;{' '}
                {seller.hasAccount && (
                  <>{t('auth.username')}: <span className="font-mono">{seller.username}</span> &middot;{' '}</>
                )}
              </p>
            </div>
          )}
        </div>
        <Button
          onClick={() => { setSyncResult(null); syncStatsMutation.mutate(); }}
          disabled={syncStatsMutation.isPending}
          size="sm"
        >
          {syncStatsMutation.isPending ? t('common.loading') : t('admin.syncStats')}
        </Button>
      </div>

      {syncResult && (
        <pre className="max-h-32 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-300">
          {syncResult}
        </pre>
      )}

      {/* Fee Settings */}
      {seller && <FeeEditor seller={seller} companyId={activeCompanyId!} onUpdated={() => queryClient.invalidateQueries({ queryKey: ['admin', 'seller', sellerId] })} />}

      {/* Sub-sellers */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('seller.subSellersTitle')} ({subSellers.length})</CardTitle>
            <Button size="sm" onClick={() => setShowCreateSubSeller(true)}>
              {t('sellers.addSubSeller')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {subSellersLoading && <div className="p-4 text-gray-400">{t('common.loading')}</div>}
          {!subSellersLoading && subSellers.length === 0 && (
            <div className="p-4 text-gray-400">{t('common.noData')}</div>
          )}
          {subSellers.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('sellers.name')}</TableHead>
                  <TableHead>{t('auth.username')}</TableHead>
                  <TableHead>{t('seller.level')}</TableHead>
                  <TableHead className="text-right">{t('sellers.payinFee')}</TableHead>
                  <TableHead className="text-right">{t('sellers.payoutFee')}</TableHead>
                  <TableHead>{t('sellers.status')}</TableHead>
                  <TableHead>{t('sellers.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subSellers.map((ss) => (
                  <TableRow key={ss.id}>
                    <TableCell className="font-medium">{ss.name}</TableCell>
                    <TableCell className="font-mono text-sm">{ss.username ?? '-'}</TableCell>
                    <TableCell>{ss.level}</TableCell>
                    <TableCell className="text-right">{ss.payinSharePercent}%</TableCell>
                    <TableCell className="text-right">{ss.payoutSharePercent}%</TableCell>
                    <TableCell>
                      <Badge variant={ss.status === 'active' ? 'success' : 'danger'}>
                        {t(`common.${ss.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (window.confirm(t('sellers.confirmReset', { name: ss.name }))) {
                            resetSubSellerPwMutation.mutate(ss.id);
                          }
                        }}
                        disabled={resetSubSellerPwMutation.isPending}
                      >
                        {t('sellers.resetPassword')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Transaction Stats */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('transactions.title')}</CardTitle>
            <div className="flex items-center gap-3">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {dateFrom} ~ {dateTo}
              </span>
            </div>
          </div>
        </CardHeader>
        {stats.length > 0 && seller && (seller.payinFeePercent > 0 || seller.payoutFeePercent > 0) && (
          <div className="grid grid-cols-2 gap-4 px-6 pb-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950">
              <div className="text-xs font-medium text-blue-600 dark:text-blue-400">
                {t('transactions.calcPayinFee')} ({seller.payinFeePercent}%)
              </div>
              <div className="mt-1 text-xl font-bold text-blue-700 dark:text-blue-300">
                {formatNumber(stats.reduce((s, r) => s + (r.deposit + r.topup) * seller.payinFeePercent / 100, 0))}
              </div>
              <div className="mt-0.5 text-xs text-blue-500 dark:text-blue-400">
                ({t('transactions.deposit')} + {t('transactions.topup')}) × {seller.payinFeePercent}%
              </div>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
              <div className="text-xs font-medium text-green-600 dark:text-green-400">
                {t('transactions.calcPayoutFee')} ({seller.payoutFeePercent}%)
              </div>
              <div className="mt-1 text-xl font-bold text-green-700 dark:text-green-300">
                {formatNumber(stats.reduce((s, r) => s + (r.withdraw + r.settle) * seller.payoutFeePercent / 100, 0))}
              </div>
              <div className="mt-0.5 text-xs text-green-500 dark:text-green-400">
                ({t('transactions.withdraw')} + {t('transactions.settle')}) × {seller.payoutFeePercent}%
              </div>
            </div>
          </div>
        )}
        <CardContent className="p-0">
          {statsLoading && <div className="p-4 text-gray-400">{t('common.loading')}</div>}
          {!statsLoading && stats.length === 0 && (
            <div className="p-4 text-gray-400">{t('common.noData')}</div>
          )}
          {stats.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('transactions.date')}</TableHead>
                    <TableHead className="text-right">{t('transactions.deposit')}</TableHead>
                    <TableHead className="text-right">{t('transactions.depositCount')}</TableHead>
                    <TableHead className="text-right">{t('transactions.topup')}</TableHead>
                    <TableHead className="text-right">{t('transactions.topupCount')}</TableHead>
                    <TableHead className="text-right">{t('transactions.withdraw')}</TableHead>
                    <TableHead className="text-right">{t('transactions.withdrawCount')}</TableHead>
                    <TableHead className="text-right">{t('transactions.settle')}</TableHead>
                    <TableHead className="text-right">{t('transactions.hold')}</TableHead>
                    <TableHead className="text-right">{t('transactions.feeSeller')}</TableHead>
                    <TableHead className="text-right">{t('transactions.feePlatform')}</TableHead>
                    <TableHead className="text-right">{t('transactions.fee')}</TableHead>
                    <TableHead className="text-right text-blue-600 dark:text-blue-400">{t('transactions.calcPayinFee')}</TableHead>
                    <TableHead className="text-right text-green-600 dark:text-green-400">{t('transactions.calcPayoutFee')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const filtered = stats.filter((s) => s.deposit || s.topup || s.withdraw || s.settle || s.fee);
                    const payinPct = seller?.payinFeePercent ?? 0;
                    const payoutPct = seller?.payoutFeePercent ?? 0;
                    // Group every 7 days starting from oldest record
                    // Data is sorted desc, so oldest is last
                    const oldestDate = filtered.length > 0 ? new Date(filtered[filtered.length - 1].date) : new Date();
                    const getWeekGroup = (dateStr: string) => {
                      const diff = Math.floor((new Date(dateStr).getTime() - oldestDate.getTime()) / (86400000));
                      return Math.floor(diff / 7);
                    };

                    const weekGroups: { week: number; items: StatItem[] }[] = [];
                    let currentWeek: number | null = null;
                    let currentItems: StatItem[] = [];

                    for (const stat of filtered) {
                      const week = getWeekGroup(formatStatDate(stat.date));
                      if (currentWeek !== null && week !== currentWeek) {
                        weekGroups.push({ week: currentWeek, items: currentItems });
                        currentItems = [];
                      }
                      currentWeek = week;
                      currentItems.push(stat);
                    }
                    if (currentItems.length > 0 && currentWeek !== null) {
                      weekGroups.push({ week: currentWeek, items: currentItems });
                    }

                    const totalWeeks = weekGroups.length;
                    const rows: React.ReactNode[] = [];

                    weekGroups.forEach((group, idx) => {
                      const weekNum = totalWeeks - idx;
                      const dates = group.items.map((s) => formatStatDate(s.date));
                      const dateRange = `${dates[dates.length - 1]} ~ ${dates[0]}`;

                      for (const stat of group.items) {
                        rows.push(
                          <TableRow key={stat.id}>
                            <TableCell className="font-mono text-sm whitespace-nowrap">{formatStatDate(stat.date)}</TableCell>
                            <TableCell className="text-right">{formatNumber(stat.deposit)}</TableCell>
                            <TableCell className="text-right text-gray-500">{stat.depositCount}</TableCell>
                            <TableCell className="text-right">{formatNumber(stat.topup)}</TableCell>
                            <TableCell className="text-right text-gray-500">{stat.topupCount}</TableCell>
                            <TableCell className="text-right">{formatNumber(stat.withdraw)}</TableCell>
                            <TableCell className="text-right text-gray-500">{stat.withdrawCount}</TableCell>
                            <TableCell className="text-right">{formatNumber(stat.settle)}</TableCell>
                            <TableCell className="text-right">{formatNumber(stat.hold)}</TableCell>
                            <TableCell className="text-right font-medium text-primary">{formatNumber(stat.feeSeller)}</TableCell>
                            <TableCell className="text-right">{formatNumber(stat.feePlatform)}</TableCell>
                            <TableCell className="text-right font-medium">{formatNumber(stat.fee)}</TableCell>
                            <TableCell className="text-right font-medium text-blue-600 dark:text-blue-400">
                              {formatNumber((stat.deposit + stat.topup) * payinPct / 100)}
                            </TableCell>
                            <TableCell className="text-right font-medium text-green-600 dark:text-green-400">
                              {formatNumber((stat.withdraw + stat.settle) * payoutPct / 100)}
                            </TableCell>
                          </TableRow>,
                        );
                      }

                      rows.push(
                        <TableRow key={`week-${group.week}`} className="border-t border-amber-300 bg-amber-50 font-semibold dark:border-amber-700 dark:bg-amber-950">
                          <TableCell className="text-amber-800 dark:text-amber-200 whitespace-nowrap">{t('transactions.weeklySummary', { week: weekNum })} ({dateRange})</TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">{formatNumber(group.items.reduce((s, r) => s + r.deposit, 0))}</TableCell>
                          <TableCell className="text-right text-amber-600 dark:text-amber-400">{group.items.reduce((s, r) => s + r.depositCount, 0)}</TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">{formatNumber(group.items.reduce((s, r) => s + r.topup, 0))}</TableCell>
                          <TableCell className="text-right text-amber-600 dark:text-amber-400">{group.items.reduce((s, r) => s + r.topupCount, 0)}</TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">{formatNumber(group.items.reduce((s, r) => s + r.withdraw, 0))}</TableCell>
                          <TableCell className="text-right text-amber-600 dark:text-amber-400">{group.items.reduce((s, r) => s + r.withdrawCount, 0)}</TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">{formatNumber(group.items.reduce((s, r) => s + r.settle, 0))}</TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">{formatNumber(group.items.reduce((s, r) => s + r.hold, 0))}</TableCell>
                          <TableCell className="text-right text-primary">{formatNumber(group.items.reduce((s, r) => s + r.feeSeller, 0))}</TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">{formatNumber(group.items.reduce((s, r) => s + r.feePlatform, 0))}</TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">{formatNumber(group.items.reduce((s, r) => s + r.fee, 0))}</TableCell>
                          <TableCell className="text-right text-blue-600 dark:text-blue-400">{formatNumber(group.items.reduce((s, r) => s + (r.deposit + r.topup) * payinPct / 100, 0))}</TableCell>
                          <TableCell className="text-right text-green-600 dark:text-green-400">{formatNumber(group.items.reduce((s, r) => s + (r.withdraw + r.settle) * payoutPct / 100, 0))}</TableCell>
                        </TableRow>,
                      );
                    });

                    return rows;
                  })()}
                  {/* Monthly total */}
                  <TableRow className="border-t-2 border-gray-300 bg-gray-100 text-base font-bold dark:border-gray-600 dark:bg-gray-700">
                    <TableCell className="text-gray-900 dark:text-gray-100">{t('transactions.total')}</TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">{formatNumber(stats.reduce((s, r) => s + r.deposit, 0))}</TableCell>
                    <TableCell className="text-right text-gray-600 dark:text-gray-300">{stats.reduce((s, r) => s + r.depositCount, 0)}</TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">{formatNumber(stats.reduce((s, r) => s + r.topup, 0))}</TableCell>
                    <TableCell className="text-right text-gray-600 dark:text-gray-300">{stats.reduce((s, r) => s + r.topupCount, 0)}</TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">{formatNumber(stats.reduce((s, r) => s + r.withdraw, 0))}</TableCell>
                    <TableCell className="text-right text-gray-600 dark:text-gray-300">{stats.reduce((s, r) => s + r.withdrawCount, 0)}</TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">{formatNumber(stats.reduce((s, r) => s + r.settle, 0))}</TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">{formatNumber(stats.reduce((s, r) => s + r.hold, 0))}</TableCell>
                    <TableCell className="text-right text-primary">{formatNumber(stats.reduce((s, r) => s + r.feeSeller, 0))}</TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">{formatNumber(stats.reduce((s, r) => s + r.feePlatform, 0))}</TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">{formatNumber(stats.reduce((s, r) => s + r.fee, 0))}</TableCell>
                    <TableCell className="text-right text-blue-600 dark:text-blue-400">
                      {formatNumber(stats.reduce((s, r) => s + (r.deposit + r.topup) * (seller?.payinFeePercent ?? 0) / 100, 0))}
                    </TableCell>
                    <TableCell className="text-right text-green-600 dark:text-green-400">
                      {formatNumber(stats.reduce((s, r) => s + (r.withdraw + r.settle) * (seller?.payoutFeePercent ?? 0) / 100, 0))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sub-Seller Sharing Breakdown */}
      {stats.length > 0 && subSellers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('transactions.subSellerSharing')}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('sellers.name')}</TableHead>
                    <TableHead className="text-right">{t('sellers.payinFee')} %</TableHead>
                    <TableHead className="text-right text-blue-600 dark:text-blue-400">{t('transactions.calcPayinFee')}</TableHead>
                    <TableHead className="text-right">{t('sellers.payoutFee')} %</TableHead>
                    <TableHead className="text-right text-green-600 dark:text-green-400">{t('transactions.calcPayoutFee')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const filtered = stats.filter((s) => s.deposit || s.topup || s.withdraw || s.settle || s.fee);
                    const oldestD = filtered.length > 0 ? new Date(filtered[filtered.length - 1].date) : new Date();
                    const getWG = (dateStr: string) => Math.floor((new Date(dateStr).getTime() - oldestD.getTime()) / 86400000 / 7);
                    const weekGroups: { week: number; items: StatItem[] }[] = [];
                    let curWeek: number | null = null;
                    let curItems: StatItem[] = [];
                    for (const stat of filtered) {
                      const w = getWG(formatStatDate(stat.date));
                      if (curWeek !== null && w !== curWeek) {
                        weekGroups.push({ week: curWeek, items: curItems });
                        curItems = [];
                      }
                      curWeek = w;
                      curItems.push(stat);
                    }
                    if (curItems.length > 0 && curWeek !== null) weekGroups.push({ week: curWeek, items: curItems });
                    const totalWeeks = weekGroups.length;

                    const rows: React.ReactNode[] = [];

                    // Weekly breakdowns
                    weekGroups.forEach((group, idx) => {
                      const weekNum = totalWeeks - idx;
                      const dates = group.items.map((s) => formatStatDate(s.date));
                      const dateRange = `${dates[dates.length - 1]} ~ ${dates[0]}`;
                      const wDeposit = group.items.reduce((s, r) => s + r.deposit, 0);
                      const wTopup = group.items.reduce((s, r) => s + r.topup, 0);
                      const wWithdraw = group.items.reduce((s, r) => s + r.withdraw, 0);
                      const wSettle = group.items.reduce((s, r) => s + r.settle, 0);

                      // Week header
                      rows.push(
                        <TableRow key={`wh-${group.week}`} className="border-t border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950">
                          <TableCell className="font-semibold text-amber-800 dark:text-amber-200">
                            {t('transactions.weeklySummary', { week: weekNum })} ({dateRange})
                          </TableCell>
                          <TableCell>{''}</TableCell>
                          <TableCell>{''}</TableCell>
                          <TableCell>{''}</TableCell>
                          <TableCell>{''}</TableCell>
                        </TableRow>,
                      );

                      // Per sub-seller for this week
                      subSellers.forEach((ss) => {
                        const payinAmt = (wDeposit + wTopup) * ss.payinSharePercent / 100;
                        const payoutAmt = (wWithdraw + wSettle) * ss.payoutSharePercent / 100;
                        rows.push(
                          <TableRow key={`w-${group.week}-${ss.id}`}>
                            <TableCell className="pl-6">{ss.name}</TableCell>
                            <TableCell className="text-right">{ss.payinSharePercent}%</TableCell>
                            <TableCell className="text-right font-medium text-blue-600 dark:text-blue-400">{formatNumber(payinAmt)}</TableCell>
                            <TableCell className="text-right">{ss.payoutSharePercent}%</TableCell>
                            <TableCell className="text-right font-medium text-green-600 dark:text-green-400">{formatNumber(payoutAmt)}</TableCell>
                          </TableRow>,
                        );
                      });
                    });

                    // Monthly total
                    const totalDeposit = stats.reduce((s, r) => s + r.deposit, 0);
                    const totalTopup = stats.reduce((s, r) => s + r.topup, 0);
                    const totalWithdraw = stats.reduce((s, r) => s + r.withdraw, 0);
                    const totalSettle = stats.reduce((s, r) => s + r.settle, 0);
                    const totalPayinShare = subSellers.reduce((s, ss) => s + ss.payinSharePercent, 0);
                    const totalPayoutShare = subSellers.reduce((s, ss) => s + ss.payoutSharePercent, 0);
                    const totalPayinAmount = subSellers.reduce((s, ss) => s + (totalDeposit + totalTopup) * ss.payinSharePercent / 100, 0);
                    const totalPayoutAmount = subSellers.reduce((s, ss) => s + (totalWithdraw + totalSettle) * ss.payoutSharePercent / 100, 0);
                    rows.push(
                      <TableRow key="sharing-total" className="border-t-2 border-gray-300 bg-gray-100 text-base font-bold dark:border-gray-600 dark:bg-gray-700">
                        <TableCell className="text-gray-900 dark:text-gray-100">{t('transactions.total')}</TableCell>
                        <TableCell className="text-right text-gray-900 dark:text-gray-100">{totalPayinShare}%</TableCell>
                        <TableCell className="text-right text-blue-600 dark:text-blue-400">{formatNumber(totalPayinAmount)}</TableCell>
                        <TableCell className="text-right text-gray-900 dark:text-gray-100">{totalPayoutShare}%</TableCell>
                        <TableCell className="text-right text-green-600 dark:text-green-400">{formatNumber(totalPayoutAmount)}</TableCell>
                      </TableRow>,
                    );
                    return rows;
                  })()}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {showCreateSubSeller && activeCompanyId && sellerId && (
        <CreateSubSellerDialog
          sellerId={sellerId}
          companyId={activeCompanyId}
          onClose={() => setShowCreateSubSeller(false)}
          onCreated={(result) => {
            setCredentialResult(result);
            queryClient.invalidateQueries({ queryKey: ['admin', 'seller-sub-sellers', sellerId] });
          }}
        />
      )}

      {credentialResult && (
        <CredentialResultDialog
          result={credentialResult}
          onClose={() => setCredentialResult(null)}
        />
      )}

      {subSellerCredential && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('sellers.credentialTitle')}
            </h3>
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
              {t('sellers.credentialWarning')}
            </div>
            <div className="mb-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('sellers.name')}</label>
                <div className="font-medium text-gray-900 dark:text-gray-100">{subSellerCredential.subSellerName}</div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('auth.username')}</label>
                <div className="rounded bg-gray-100 px-3 py-2 font-mono text-sm dark:bg-gray-700 dark:text-gray-100">{subSellerCredential.username}</div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('sellers.tempPassword')}</label>
                <div className="rounded bg-gray-100 px-3 py-2 font-mono text-sm font-bold dark:bg-gray-700 dark:text-gray-100">{subSellerCredential.tempPassword}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={() => {
                  navigator.clipboard.writeText(
                    t('sellers.credentialCopyFormat', {
                      name: subSellerCredential.subSellerName,
                      username: subSellerCredential.username,
                      password: subSellerCredential.tempPassword,
                    }),
                  );
                }}
              >
                {t('sellers.copyCredentials')}
              </Button>
              <Button variant="ghost" onClick={() => setSubSellerCredential(null)}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
